import { Box, Button, TextField } from "@material-ui/core"
import { useEffect, useState } from "react"
import type { ChangeEvent, ReactElement } from "react"
import type { Bundle } from "../../../redux"
import { Body1, SubTitle1 } from "../../atoms"
import { BundleSelector, DialogWindow, DialogWindowHeader } from "../../molecules"

import "./project-edit-dialog.styles.scss"
import { useTranslation } from "react-i18next"
import { mapObjectDescriptorToTranslation } from "../../../utils"

export interface IProjectEditDialogProps {
  title?: string
  dialogTitle: string
  message: string
  entityName: "map" | "comparison"
  bundles: Bundle[]
  open: boolean
  onDontSave?: () => void
  onClose: () => void
  onSave: (title: string, bundles: Bundle[]) => void
}

export function ProjectEditDialog(props: IProjectEditDialogProps): ReactElement {
  const { title, bundles, open, dialogTitle, message, entityName, onDontSave, onClose, onSave } = props

  const { t } = useTranslation(["general", "project"])

  const [selectedBundles, setSelectedBundles] = useState<Bundle[]>(bundles)
  const [titleInputText, setTitleInputText] = useState<string | undefined>(title)

  useEffect(() => {
    setSelectedBundles(bundles)
    setTitleInputText(title)
  }, [title, bundles])

  function onTitleChange(event: ChangeEvent<HTMLInputElement>) {
    const title: string = event.target.value
    setTitleInputText(title)
  }

  function onClickSave() {
    if (titleInputText) onSave(titleInputText, selectedBundles)
    onClose()
  }

  return (
    <DialogWindow open={open} onClose={onClose} className="project-edit-dialog">
      <DialogWindowHeader title={dialogTitle} onClose={onClose} />
      <Box className="project-edit-dialog-content">
        <Body1>{message}</Body1>
        <Box className="textfield">
          <Box className="label">
            <SubTitle1>
              <b>
                {t("project:titleMap", {
                  project: mapObjectDescriptorToTranslation(entityName)
                })}
              </b>
            </SubTitle1>
            <SubTitle1>({t("general:required").toLowerCase()})</SubTitle1>
          </Box>
          <TextField
            autoFocus
            variant="outlined"
            color="primary"
            type="text"
            value={titleInputText ? titleInputText : ""}
            onChange={onTitleChange}
            fullWidth
            required
            onFocus={(e) => e.target.select()}
          />
        </Box>
        <Box className="autocomplete-label">
          <SubTitle1>
            <b>{t("project:saveInFolder")}</b>
          </SubTitle1>
        </Box>
        <BundleSelector selectedBundles={selectedBundles} onChange={setSelectedBundles} />
      </Box>
      <Box className="footer">
        <Button color="primary" onClick={onClose}>
          {t("general:dialog.cancel")}
        </Button>
        {onDontSave && (
          <Button color="primary" variant="outlined" onClick={onDontSave}>
            {t("general:notSave")}
          </Button>
        )}
        <Button variant="contained" color="primary" onClick={onClickSave}>
          {t("general:save")}
        </Button>
      </Box>
    </DialogWindow>
  )
}
